import { useEffect, useRef } from "react";
import { VideoData } from "../types";
import { IN_FLIGHT_STATUSES } from "../constants/taskStatuses";

// How long a video can sit "processing" before we show a gentle "taking longer than
// usual" hint. NOT a delete trigger — see the history note below.
const UPLOAD_SLOW_HINT_MS = Number(process.env.REACT_APP_UPLOAD_TIMEOUT_MS) || 2 * 60 * 60 * 1000;

interface UseUploadTimeoutParams {
  videos: VideoData[];
  setVideos: React.Dispatch<React.SetStateAction<VideoData[]>>;
  onError: (message: string) => void;
  error: unknown;
}

/**
 * Surfaces a non-destructive "this upload is taking longer than usual" hint for videos
 * that stay in-flight past UPLOAD_SLOW_HINT_MS.
 *
 * HISTORY (2026-07-08): this hook used to POST a REAL delete (DB row + S3 file) on
 * timeout and drop the video from the list. A client-side wall-clock cannot tell
 * "stuck" from "busy", so under normal load (big videos, a queue behind one worker,
 * a transient web-tier hiccup) it silently destroyed uploads the backend was still
 * processing — it deleted two of Isaac's real videos. The destructive path is removed:
 * only the backend, which knows real task state, ever deletes. This hint is informational.
 */
export function useUploadTimeout({ videos, setVideos, onError, error }: UseUploadTimeoutParams) {
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const hinted = useRef<Set<number>>(new Set());

  // On a library fetch error, keep the user's in-flight uploads visible (they may still
  // complete once the transient error clears). No destructive action.
  useEffect(() => {
    void error;
  }, [error]);

  useEffect(() => {
    videos.forEach((video) => {
      if (!video.task_status || !IN_FLIGHT_STATUSES.has(video.task_status)) return;
      if (timers.current.has(video.id) || hinted.current.has(video.id)) return;

      const timer = setTimeout(() => {
        timers.current.delete(video.id);
        hinted.current.add(video.id);
        // Non-destructive: inform, but leave the video in place. The backend finishes
        // it (or marks it failed) on its own schedule.
        onError(
          "One of your uploads is taking longer than usual. Large or queued videos can " +
            "take a while — it will finish on its own, no need to re-upload."
        );
      }, UPLOAD_SLOW_HINT_MS);

      timers.current.set(video.id, timer);
    });

    // Clear timers for videos that finished (or left the list) before the hint fired.
    timers.current.forEach((timer, id) => {
      const video = videos.find((v) => v.id === id);
      const isGone = !video;
      const isDone = video && video.task_status && !IN_FLIGHT_STATUSES.has(video.task_status);
      if (isGone || isDone) {
        clearTimeout(timer);
        timers.current.delete(id);
      }
      if (isDone) hinted.current.delete(id);
    });
  }, [videos, setVideos, onError]);

  useEffect(() => {
    const active = timers.current;
    return () => {
      active.forEach(clearTimeout);
      active.clear();
    };
  }, []);
}
